// src/components/admin/common/CloudinaryTest.tsx
import React, { useState } from 'react';
import { Upload, Check, AlertCircle } from 'lucide-react';

const CloudinaryTest = () => {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState({ success: false, url: '', error: '' });

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;
    const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;

    console.log('Starting upload with:', {
      cloudName,
      uploadPreset,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type
    });

    if (!uploadPreset || !cloudName) {
      setResult({ 
        success: false, 
        url: '', 
        error: 'Missing Cloudinary configuration' 
      });
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', uploadPreset);

    setUploading(true);
    setResult({ success: false, url: '', error: '' });

    const uploadUrl = `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`;
    console.log('Uploading to:', uploadUrl);

    try {
      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
      });

      const responseText = await response.text();
      console.log('Raw response:', responseText);

      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse response:', e);
        throw new Error('Invalid response from server');
      }

      console.log('Parsed response:', responseData);

      if (!response.ok) {
        throw new Error(responseData.error?.message || 'Upload failed');
      }

      if (responseData.secure_url) {
        setResult({
          success: true,
          url: responseData.secure_url,
          error: ''
        });
      } else {
        throw new Error('No URL in response');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setResult({
        success: false,
        url: '',
        error: error instanceof Error ? error.message : 'Upload failed'
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto p-6 bg-white rounded-lg shadow-sm">
      <h2 className="text-xl font-bold mb-4">Cloudinary Upload Test</h2>
      
      <div className="space-y-4">
        <div className="flex justify-center">
          <label className="cursor-pointer">
            <div className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 
              transition-colors flex items-center gap-2">
              <Upload className="w-5 h-5" />
              {uploading ? 'Uploading...' : 'Select Image'}
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={handleUpload}
              disabled={uploading}
              className="hidden"
            />
          </label>
        </div>

        {uploading && (
          <div className="flex items-center justify-center gap-2 text-gray-600">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent" />
            Uploading...
          </div>
        )}

        {result.error && (
          <div className="flex items-center gap-2 text-red-500 bg-red-50 p-4 rounded-lg">
            <AlertCircle className="w-5 h-5" />
            {result.error}
          </div>
        )}

        {result.success && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-green-500 bg-green-50 p-4 rounded-lg">
              <Check className="w-5 h-5" />
              Upload successful!
            </div>
            
            <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
              <img 
                src={result.url} 
                alt="Uploaded" 
                className="w-full h-full object-contain"
              />
            </div>
            
            <div className="break-all text-sm text-gray-600">
              <p className="font-medium">Image URL:</p>
              <p>{result.url}</p>
            </div>
          </div>
        )}

        <div className="mt-4 p-4 bg-gray-50 rounded-lg text-sm text-gray-600">
          <p className="font-medium mb-2">Debug Info:</p>
          <div className="space-y-1">
            <p>Cloud Name: {process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'Not set'}</p>
            <p>Upload Preset: {process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'Not set'}</p>
            <p>Upload URL: https://api.cloudinary.com/v1_1/{process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CloudinaryTest;